import { atom } from 'jotai';

import {
  Namespace,
  Ref,
  Sequence,
  SequencerEditorConfig,
  SequencerEditorConfigType,
  SequenceRef,
  Setting,
  SettingValue,
} from '@repo/sdk/models/components';

import { evaluateConditionExpression } from '@/features/equipment-browser/conditional-evaluator/condition-evaluator';
import {
  extractReadingsRefsFromSequencerEditorConfig,
  extractSettingsRefsFromSequencerEditorConfig,
  extractSiblingSettingsRefsFromSequencerEditorConfig,
} from '@/features/equipment-browser/sequencer-section/extract-sequencer-editor-refs';
import {
  mapReadingRefsToSubscriptionIdentifiers,
  mapSettingRefsToSubscriptionIdentifiers,
} from '@/features/equipment-browser/subscriptions/map-to-subscription-identifiers';
import { SubscriptionIdentifier } from '@/hooks/query-hooks/gaia-api/use-subscription-base';
import { readingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-readings-stream';
import { settingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-settings-stream';
import { LatestValue } from '@/types/reading-types';

import { equipmentResponseAtom } from './equipment-response.state';

export interface SequenceWithValues {
  key: string;
  label: string;
  value?: Sequence;
  ref?: Ref;
}

const parsePositiveStageNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 0 ? value : undefined;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }

  return undefined;
};

/**
 * Atom that provides the sequencer editor configuration from the equipment response.
 *
 * @remarks
 * This atom derives the sequencer editor configuration from the equipment response.
 * The configuration determines the type and structure of the sequencer editor UI,
 * including references to current stage, data, manager state, and seasonal sequences.
 *
 * @returns The sequencer editor configuration if available, otherwise undefined
 *
 * @see {@link SequencerEditorConfig} for the configuration structure
 * @see {@link equipmentResponseAtom} for the source equipment data
 */
export const sequencerEditorConfigAtom = atom<SequencerEditorConfig | undefined>((get) => {
  const equipment = get(equipmentResponseAtom);

  return equipment?.sequencerEditorConfig;
});
sequencerEditorConfigAtom.debugLabel = 'sequencerEditorConfigAtom';

/**
 * Atom that provides subscription identifiers for readings in the sequencer editor.
 *
 * @remarks
 * This atom extracts all reading references (non-Setting namespace) from the sequencer editor
 * configuration and maps them to subscription identifiers. These identifiers are used by the
 * subscription system to establish real-time connections for monitoring sequencer state,
 * current stage, data values, and seasonal sequence readings.
 *
 * The context key 'active-sequencer-reading' is used to group these subscriptions for
 * efficient management and cleanup.
 *
 * @returns Array of subscription identifiers for all reading refs in the sequencer editor config
 *
 * @see {@link extractReadingsRefsFromSequencerEditorConfig} for the extraction logic
 * @see {@link mapReadingRefsToSubscriptionIdentifiers} for the mapping logic
 * @see {@link sequencerEditorConfigAtom} for the source configuration
 */
export const sequencerEditorReadingsIdentifiersAtom = atom<SubscriptionIdentifier[]>((get) => {
  const sequencerEditorConfig = get(sequencerEditorConfigAtom);
  return mapReadingRefsToSubscriptionIdentifiers(
    extractReadingsRefsFromSequencerEditorConfig(sequencerEditorConfig),
    'sequencer-editor-reading'
  );
});
sequencerEditorReadingsIdentifiersAtom.debugLabel = 'sequencerEditorReadingsIdentifiersAtom';

/**
 * Atom that provides subscription identifiers for settings in the sequencer editor.
 *
 * @remarks
 * This atom extracts all setting references (Setting namespace only) from the sequencer editor
 * configuration and maps them to subscription identifiers. These identifiers are used by the
 * subscription system to establish real-time connections for monitoring and updating sequencer
 * configuration settings, such as stage modes, sequence IDs, and operational parameters.
 *
 * The context key 'active-sequencer-setting' is used to group these subscriptions for
 * efficient management and cleanup.
 *
 * @returns Array of subscription identifiers for all setting refs in the sequencer editor config
 *
 * @see {@link extractSettingsRefsFromSequencerEditorConfig} for the extraction logic
 * @see {@link mapSettingRefsToSubscriptionIdentifiers} for the mapping logic
 * @see {@link sequencerEditorConfigAtom} for the source configuration
 */
export const sequencerEditorSettingsIdentifiersAtom = atom<SubscriptionIdentifier[]>((get) => {
  const sequencerEditorConfig = get(sequencerEditorConfigAtom);
  return mapSettingRefsToSubscriptionIdentifiers(
    extractSettingsRefsFromSequencerEditorConfig(sequencerEditorConfig),
    'sequencer-editor-setting'
  );
});
sequencerEditorSettingsIdentifiersAtom.debugLabel = 'sequencerEditorSettingsIdentifiersAtom';

export const siblingSequencerSettingsIdentifiersAtom = atom<SubscriptionIdentifier[]>((get) => {
  const sequencerEditorConfig = get(sequencerEditorConfigAtom);
  return mapSettingRefsToSubscriptionIdentifiers(
    extractSiblingSettingsRefsFromSequencerEditorConfig(sequencerEditorConfig),
    'sibling-sequencer-setting'
  );
});
siblingSequencerSettingsIdentifiersAtom.debugLabel = 'siblingSequencerSettingsIdentifiersAtom';

/**
 * Atom that provides the sequences from the sequencer editor configuration.
 *
 * @remarks
 * This atom returns the sequences array from the sequencer editor configuration.
 * Each sequence contains a key and a DisplayRef which includes both a label and a ref.
 *
 * For different sequencer types:
 * - **BedrockV1**: Single sequence with the sequencer data
 * - **BedrockV2**: Two sequences (Summer and Winter)
 * - **PureTemplateV1**: Single sequence
 * - **PureTemplateV2**: Two sequences (Summer and Winter)
 *
 * @returns Array of SequenceRef objects, or undefined if not available
 *
 * @see {@link SequencerEditorConfigType} for the available configuration types
 * @see {@link sequencerEditorConfigAtom} for the source configuration
 *
 * @example
 * ```ts
 * // BedrockV2 example
 * // Returns: [
 * //   { key: 'Summer_Sequence', displayRef: { label: 'Summer Sequence', ref: { id: 'summer', ... } } },
 * //   { key: 'Winter_Sequence', displayRef: { label: 'Winter Sequence', ref: { id: 'winter', ... } } }
 * // ]
 * ```
 */
export const getSequencerEditorDataAtom = atom((get) => {
  const sequencerEditorConfig = get(sequencerEditorConfigAtom);

  return sequencerEditorConfig?.sequences;
});
getSequencerEditorDataAtom.debugLabel = 'getSequencerEditorDataAtom';

/**
 * Atom that provides the actual reading values for all readings in the sequencer editor.
 *
 * @remarks
 * This atom maps the sequencer editor reading identifiers to their actual latest values
 * by using the readingsFamily atom. It provides real-time access to all reading data
 * needed for the sequencer editor, including current stage, data refs, and sequence refs.
 *
 * Each item in the returned array contains:
 * - `id`: The reading identifier
 * - `value`: The latest value from the readings stream
 *
 * @returns Array of objects containing reading IDs and their corresponding latest values
 *
 * @see {@link sequencerEditorReadingsIdentifiersAtom} for the source identifiers
 * @see {@link readingsFamily} for the atom family that provides reading values
 */
export const sequencerEditorReadingValuesAtom = atom<Array<{ id: string; value: LatestValue }>>(
  (get) => {
    const readingIdentifiers = get(sequencerEditorReadingsIdentifiersAtom);

    return readingIdentifiers.map((identifier: SubscriptionIdentifier) => {
      const latestValue = get(readingsFamily({ pointId: identifier.id } as LatestValue));

      return {
        id: identifier.id,
        value: latestValue,
      };
    });
  }
);
sequencerEditorReadingValuesAtom.debugLabel = 'sequencerEditorReadingValuesAtom';

/**
 * Atom that provides the actual setting values for all settings in the sequencer editor.
 *
 * @remarks
 * This atom maps the sequencer editor setting identifiers to their actual setting values
 * by using the settingsFamily atom. It provides real-time access to all setting data
 * needed for the sequencer editor configuration.
 *
 * Each item in the returned array contains:
 * - `id`: The setting identifier
 * - `value`: The setting value from the settings stream
 *
 * @returns Array of objects containing setting IDs and their corresponding values
 *
 * @see {@link sequencerEditorSettingsIdentifiersAtom} for the source identifiers
 * @see {@link settingsFamily} for the atom family that provides setting values
 */
export const sequencerEditorSettingValuesAtom = atom<Array<{ id: string; value: SettingValue }>>(
  (get) => {
    const settingIdentifiers = get(sequencerEditorSettingsIdentifiersAtom);

    return settingIdentifiers.map((identifier: SubscriptionIdentifier) => {
      const setting = get(settingsFamily({ settingId: identifier.id } as Setting));

      return {
        id: identifier.id,
        value: setting.value,
      };
    });
  }
);
sequencerEditorSettingValuesAtom.debugLabel = 'sequencerEditorSettingValuesAtom';

/**
 * Atom that provides the active sequence value.
 *
 * @remarks
 * This atom retrieves the value of the activeSequenceRef, which indicates
 * which sequencer is currently active (e.g., summer or winter sequence).
 *
 * The value is typically a number or string that represents the active sequence state.
 *
 * @returns The latest value of the active sequence, or undefined if not available
 *
 * @see {@link sequencerEditorConfigAtom} for the source of the activeSequenceRef
 * @see {@link readingsFamily} for how the value is fetched
 */
export const activeSequenceValueAtom = atom((get) => {
  const config = get(sequencerEditorConfigAtom);
  const activeSequenceId = config?.activeSequenceRef?.id;

  if (!activeSequenceId) {
    return undefined;
  }

  const latestValue = get(readingsFamily({ pointId: activeSequenceId } as LatestValue));

  return latestValue?.[latestValue?.kind as keyof LatestValue];
});
activeSequenceValueAtom.debugLabel = 'activeSequenceValueAtom';

/**
 * Atom that provides the current stage value.
 *
 * @remarks
 * This atom retrieves the value of the currentStageRef, which indicates
 * the current stage of the active sequencer.
 *
 * @returns The latest value of the current stage, or undefined if not available
 *
 * @see {@link sequencerEditorConfigAtom} for the source of the currentStageRef
 */
export const currentStageValueAtom = atom((get) => {
  const config = get(sequencerEditorConfigAtom);
  const currentStageId = config?.currentStageRef?.id;

  if (!currentStageId) {
    return undefined;
  }

  const latestValue = get(readingsFamily({ pointId: currentStageId } as LatestValue));

  return latestValue?.[latestValue?.kind as keyof LatestValue];
});
currentStageValueAtom.debugLabel = 'currentStageValueAtom';

export const cosmicActiveAtom = atom<boolean | undefined>((get) => {
  const config = get(sequencerEditorConfigAtom);

  // Recompute when any sequencer-editor-backed reading or setting changes.
  get(sequencerEditorReadingValuesAtom);
  get(sequencerEditorSettingValuesAtom);

  if (!config?.cosmicConditionExpression) {
    return undefined;
  }

  return evaluateConditionExpression(config.cosmicConditionExpression);
});
cosmicActiveAtom.debugLabel = 'cosmicActiveAtom';

export const cosmicFallbackStageValueAtom = atom<number | undefined>((get) => {
  const cosmicFallbackStageRef = get(sequencerEditorConfigAtom)?.cosmicFallbackStageRef;

  if (!cosmicFallbackStageRef) {
    return undefined;
  }

  const cosmicFallbackStageValue =
    cosmicFallbackStageRef.namespace === Namespace.Setting
      ? (() => {
          const setting = get(settingsFamily({ settingId: cosmicFallbackStageRef.id } as Setting));
          return setting?.value?.[setting?.kind as keyof SettingValue];
        })()
      : (() => {
          const latestValue = get(
            readingsFamily({ pointId: cosmicFallbackStageRef.id } as LatestValue)
          );
          return latestValue?.[latestValue?.kind as keyof LatestValue];
        })();

  return parsePositiveStageNumber(cosmicFallbackStageValue);
});
cosmicFallbackStageValueAtom.debugLabel = 'cosmicFallbackStageValueAtom';

/**
 * Atom that provides values for all sequences in the sequencer editor.
 *
 * @remarks
 * This atom maps all sequences to their actual values by looking up each sequence's
 * ref in either the readings or settings family depending on the namespace.
 *
 * Each returned object contains:
 * - `key`: The sequence key (e.g., 'Summer_Sequence', 'Winter_Sequence')
 * - `label`: The display label from the DisplayRef
 * - `value`: The actual value from the reading or setting
 * - `ref`: The original ref object
 *
 * @returns Array of sequence objects with their values, or empty array if no sequences
 *
 * @see {@link sequencerEditorConfigAtom} for the source of the sequences
 * @see {@link readingsFamily} for reading values
 * @see {@link settingsFamily} for setting values
 */
export const sequencesWithValuesAtom = atom<SequenceWithValues[]>((get) => {
  const config = get(sequencerEditorConfigAtom);
  const sequences = config?.sequences;

  if (!sequences || sequences.length === 0) {
    return [];
  }

  return sequences.map((sequence: SequenceRef): SequenceWithValues => {
    const ref = sequence.displayRef?.ref;
    if (!ref) {
      return {
        key: sequence.key,
        label: sequence.displayRef?.label,
        value: undefined,
        ref: undefined,
      } as SequenceWithValues;
    }
    let value: any;
    if (ref.namespace === Namespace.Setting) {
      const setting = get(settingsFamily({ settingId: ref.id } as Setting));
      value = setting?.value?.[setting?.kind as keyof SettingValue];
    } else {
      const latestValue = get(readingsFamily({ pointId: ref.id } as LatestValue));
      value = latestValue?.[latestValue?.kind as keyof LatestValue];
    }

    return {
      key: sequence.key,
      label: sequence.displayRef?.label,
      value,
      ref,
    } as SequenceWithValues;
  });
});
sequencesWithValuesAtom.debugLabel = 'sequencesWithValuesAtom';

export const siblingSequenceValuesAtom = atom<SequenceWithValues[]>((get) => {
  const config = get(sequencerEditorConfigAtom);
  const siblingSequences = config?.siblingSequences;

  if (!siblingSequences || siblingSequences.length === 0) {
    return [];
  }

  return siblingSequences.map((sequence): SequenceWithValues => {
    const ref = sequence.displayRef?.ref;
    if (!ref) {
      return {
        key: sequence.key,
        label: sequence.displayRef?.label,
        value: undefined,
        ref: undefined,
      } as SequenceWithValues;
    }

    const setting = get(settingsFamily({ settingId: ref.id } as Setting));
    const value = setting?.value?.[setting?.kind as keyof SettingValue];

    return {
      key: sequence.key,
      label: sequence.displayRef?.label,
      value,
      ref,
    } as SequenceWithValues;
  });
});
siblingSequenceValuesAtom.debugLabel = 'siblingSequenceValuesAtom';
